import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, take, takeUntil } from 'rxjs/operators';
import { IsfStateService } from '../../../services';
import { EntityModel } from '@data-layer/models/entities';
import { CompanyModel } from '@data-layer/models/companies';
import { CompaniesService } from '@services/companies.service';
import { IsfModel } from '@data-layer/models/isf';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { BondModel, BondRequestModel, ShipmentModel } from '@data-layer/models/common';
import { EntityBondComponent } from '@shared/entity/components';
import { FormHelper } from '@helpers/form.helper';
import { IsfHelper } from '@helpers/isf.helper';
import { enumEntityByKey } from '@data-layer/enums/entities';
import { enumBondType } from '@data-layer/enums/common';
import { enumIsfShipmentType } from '@app/data-layer/enums/isf';
import { omit } from 'lodash-es';
import { suretyCodeValidator } from '@helpers/custom-validator';

@Component({
  selector: 'desktop-shipment-details-formal',
  template: `
    <app-entity-bond
      [referenceNumber]="referenceNumber"
      [formGroup]="bondFormGroup"
      [purchaseBondForm]="purchaseBondForm"
      [bondRequest]="bondRequest"
      [errors]="errors"
      [importerIdentifierQual]="importer?.entityIdentifierQualifier"
      [importerIdentifier]="importer?.entityIdentifier"
      [canEdit]="canEditBond"
      [isDesktop]="true"
      [isEntrySummary]="false"
      [isShowOnlyActive]="true"
      [showSuretyCodeForContinuousBond]="true"
      [showRefNumberForContinuousBond]="true"
      (companyBondsNotFound)="companyBondsNotFound()"
      (updateStepperNextButton)="onUpdateStepperNextButton($event)"
    ></app-entity-bond>

    <custom-modal [(open)]="openWarningModal" [header]="'BROKER_DISTRIBUTION_TYPE.CONTINUOUS_BOND'">
      <div>
        <span [innerHTML]="'CUSTOM_CONTINUOUS_BOND_MODAL.MAIN_TEXT_START' | translate"></span>
        <a
          [routerLink]="['/companies', 'account-settings', 'company-view', company?.id]"
          [queryParams]="{ returnUrl: '/isf/' + referenceNumber }"
        >
          {{ 'CUSTOM_CONTINUOUS_BOND_MODAL.MAIN_TEXT_ACTION' | translate }}
        </a>
        <span [innerHTML]="'CUSTOM_CONTINUOUS_BOND_MODAL.MAIN_TEXT_END' | translate"></span>
      </div>
      <button footer-action type="button" class="btn btn-secondary" (click)="openWarningModal = false">
        {{ 'BUTTON.I_AM_SURE' | translate }}
      </button>
      <button footer-action type="button" class="btn btn-primary" (click)="setPurchaseBond()">
        {{ 'BUTTON.PURCHASE_BOND' | translate }}
      </button>
    </custom-modal>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesktopShipmentDetailsFormalComponent implements OnInit, OnDestroy {
  @Input() public isSideBar: boolean;
  @Input() public formGroupControlsOnUpdate: 'change' | 'blur' | 'submit' = 'blur';
  @Input() public isUpdateOnExistingBondChange: boolean = true;
  @Output() public formValidityUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public bondDataChanged: EventEmitter<BondModel> = new EventEmitter<BondModel>();
  @Output() public isPurchaseBondChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public arrivalPortChanged: EventEmitter<string> = new EventEmitter<string>();

  public formGroup: UntypedFormGroup;
  public typeControl: UntypedFormControl;
  public errors: { [name: string]: IErrorForm } = {
    code: {
      required: 'FORM_ERROR.REQUIRED',
    },
    suretyCode: {
      required: 'FORM_ERROR.REQUIRED',
      wrongSuretyCode: 'FORM_ERROR.WRONG_SURETY_CODE',
    },
    bondAmount: {
      required: 'FORM_ERROR.REQUIRED',
    },
    referenceNumber: {
      required: 'FORM_ERROR.REQUIRED',
      wrongReferenceNumber: 'FORM_ERROR.WRONG_REFERENCE_NUMBER',
    },
  };
  public enumIsfShipmentType: typeof enumIsfShipmentType = enumIsfShipmentType;
  public importer: EntityModel;
  public canEditBond = true;
  public referenceNumber: string;
  public bondFormGroup: UntypedFormGroup;
  public purchaseBondForm: UntypedFormGroup;
  public bondRequest: BondRequestModel;
  public openWarningModal: boolean;
  public company: CompanyModel;
  public bond: BondModel;
  public shipment: ShipmentModel;

  @ViewChild(EntityBondComponent, { static: false }) public entityBondComponent: EntityBondComponent;

  private isf: IsfModel;
  private selectCustomContinuousBondSubscription: Subscription;
  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(private fh: FormHelper, private isfStateService: IsfStateService, private companiesService: CompaniesService) {}

  public ngOnInit(): void {
    this.formGroup = this.fh.createIsfShipmentDetailsFormGroup();
    this.typeControl = this.formGroup.get('type') as UntypedFormControl;
    this.bondFormGroup = this.fh.createIsfBondForm(this.destroy$, this.formGroupControlsOnUpdate);
    this.purchaseBondForm = this.fh.createPurchaseBondForm();

    if (!!this.arrivalPortChanged) {
      this.arrivalPortChanged.emit(this.purchaseBondForm.get('portCode').value);
    }

    this.isf = this.isfStateService.currentIsf;
    this.referenceNumber = this.isfStateService.currentIsf ? this.isfStateService.currentIsf.referenceNumber : null;

    this.bondFormGroup.valueChanges.pipe(debounceTime(100), takeUntil(this.destroy$)).subscribe(() => {
      this.formValidityUpdated.emit(this.bondFormGroup.valid);
    });
    this.purchaseBondForm.valueChanges.pipe(debounceTime(100), takeUntil(this.destroy$)).subscribe(() => {
      this.formValidityUpdated.emit(this.purchaseBondForm.valid);
    });

    this.patchShipmentData();
    this.patchBondData();
    this.canEditBond = IsfHelper.canBondRequestEdit(this.isf.state);
    this.importer = this.isfStateService.getIsfEntityById(enumEntityByKey.IMPORTER);

    if (!this.canEditBond) {
      this.bondFormGroup.disable();
    }

    // #5117
    /*this.typeControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.updateShipmentData()),
      )
      .subscribe((isf) => (this.isfStateService.currentIsf = isf));*/

    this.bondFormGroup.valueChanges
      .pipe(
        debounceTime(100),
        takeUntil(this.destroy$),
        switchMap((bondData) => {
          this.bondDataChanged.emit(bondData);
          this.isPurchaseBondChanged.emit(this.entityBondComponent.isSelectedPurchase);
          if (this.isUpdateOnExistingBondChange) {
            return this.updateBondData();
          } else {
            return of(null);
          }
        }),
      )
      .subscribe();

    this.purchaseBondForm.valueChanges
      .pipe(
        debounceTime(100),
        distinctUntilChanged((a, b) => a.portCode === b.portCode),
        takeUntil(this.destroy$),
        switchMap((_) => {
          this.bondDataChanged.emit(null);
          this.isPurchaseBondChanged.emit(this.entityBondComponent.isSelectedPurchase);
          return this.updateBondData();
        }),
      )
      .subscribe();

    if (!!this.arrivalPortChanged) {
      this.purchaseBondForm
        .get('portCode')
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((portCode) => {
          this.arrivalPortChanged.emit(portCode);
        });
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public companyBondsNotFound(): void {
    if (this.entityBondComponent?.companyBondDisabled) {
      return;
    }
    if (this.selectCustomContinuousBondSubscription) {
      this.selectCustomContinuousBondSubscription.unsubscribe();
    }
    this.selectCustomContinuousBondSubscription = this.entityBondComponent?.selectCustomContinuousBond
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.openWarningModal = true;
      });
  }

  public setPurchaseBond() {
    this.entityBondComponent.selectedBondIndex.patchValue(this.entityBondComponent.companyBonds.length + 1);
    this.openWarningModal = false;
  }

  public onUpdateStepperNextButton(isCustomContinuosBond: boolean) {
    this.bondDataChanged.emit(null);
    this.isPurchaseBondChanged.emit(this.entityBondComponent.isSelectedPurchase);

    this.bondFormGroup.get('suretyCode').clearValidators();
    this.bondFormGroup.get('bondAmount').clearValidators();
    if (isCustomContinuosBond) {
      this.bondFormGroup.get('bondAmount').setValidators([Validators.required]);
      this.bondFormGroup.get('suretyCode').setValidators([Validators.required, suretyCodeValidator()]);
    } else {
      this.bondFormGroup.get('suretyCode').setValidators([]);
      this.bondFormGroup.get('bondAmount').setValidators([]);
    }
    this.bondFormGroup.get('suretyCode').updateValueAndValidity();
    this.bondFormGroup.get('bondAmount').updateValueAndValidity();
  }

  private patchShipmentData() {
    const shipment: ShipmentModel = this.isfStateService.getShipment();
    this.shipment = shipment;

    this.fh.patchIsfShipmentDetailsFormGroup(this.formGroup, this.isfStateService.currentIsf?.type, this.shipment);
  }

  private patchBondData() {
    const bond = this.isfStateService.getBond();
    this.bondRequest = this.isfStateService.getBondRequest();

    if (this.bondRequest && this.bondRequest.bondType === enumBondType.SINGLE_TRANSACTION) {
      this.purchaseBondForm.get('portCode').patchValue(this.bondRequest.portCode);
      if (!!this.arrivalPortChanged) {
        this.arrivalPortChanged.emit(this.purchaseBondForm.get('portCode').value);
      }
    }

    this.bond = !!bond ? new BondModel(omit(bond, ['validationMessages'])) : null;

    this.company = this.companiesService.getCompany();

    if (this.company.defaultBondReferenceNumber && (!this.bond || !this.bond.type)) {
      this.bondFormGroup.get('type').patchValue(enumBondType.CONTINUOUS);
      this.bondFormGroup.get('referenceNumber').patchValue(this.company.defaultBondReferenceNumber);
    }

    if (!!this.bond && !!this.bond.type) {
      this.bondFormGroup.patchValue({
        ...this.bond,
      });
    }
  }

  private updateShipmentData(): Observable<IsfModel> {
    return this.isfStateService
      .updateIsf({
        ...this.isfStateService.currentIsf,
        importerId: this.isfStateService.currentIsf?.importer?.id,
        type: this.typeControl.value,
      })
      .pipe(take(1));
  }

  private updateBondData() {
    return this.entityBondComponent.isSelectedPurchase ? this.updateBondRequest() : this.updateBond();
  }

  private updateBond() {
    const formData = this.bondFormGroup.value;

    const { suretyCode, referenceNumber, type, bondAmount } = formData;

    const updatingBond = {
      ...this.bond,
      suretyCode,
      referenceNumber,
      type,
      bondAmount: Number(bondAmount),
    };

    return this.isfStateService.updateBond(updatingBond);
  }

  private updateBondRequest() {
    return this.isfStateService.singleBondRequest(this.entityBondComponent.purchaseBondForm.get('portCode').value);
  }
}
